import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';

import { getIsAgentEligibleForAbTestSelection } from './utils';
import debounce from 'lodash/debounce';
import i18n from 'src/locales';
import {
  agentAbTestApi,
  useUpdateAbTestConfigMutation,
} from 'src/services/agent-ab-test/agentAbTestApi';
import { CreateOrUpdateSolveABTestConfig } from 'src/services/agent-ab-test/types';
import { useGetIntentsQuery } from 'src/services/workflow-builder-metrics/workflowBuilderMetricsApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { SolveWidgetProduct } from 'src/types/types';
/**
 * Hook for updating AB test config with optimistic UI updates.
 * Updates the RTK Query cache immediately for responsive typing,
 * then debounces the actual API call by 500ms.
 *
 * On API error, the cache is reverted to the server state via
 * invalidation and an error toast is shown.
 */
export const useDebouncedUpdateAbTestConfig = () => {
  const { abTestId = '' } = useParams();
  const dispatch = useAppDispatch();
  const [updateAbTestConfig, { isLoading }] = useUpdateAbTestConfigMutation({
    fixedCacheKey: `updateAbTestConfig-${abTestId}`,
  });

  // Track the latest request sequence
  const requestSequenceRef = useRef(0);

  const handleUpdateError = useCallback(() => {
    // Invalidate cache to resync with server state, since the
    // optimistic update from updateCache() is not auto-reverted.
    dispatch(
      agentAbTestApi.util.invalidateTags([
        { id: abTestId, type: 'AbTestConfigs' },
      ]),
    );
    dispatch(
      setGlobalToastOptions({
        autoHideDuration: 4000,
        title: i18n.t('agent-ab-test:toast.failedSaveConfig'),
        variant: 'danger',
      }),
    );
  }, [abTestId, dispatch]);

  const debouncedApiCall = useMemo(
    () =>
      debounce(
        async (
          body: CreateOrUpdateSolveABTestConfig,
          sequenceNumber: number,
        ) => {
          // Only proceed if this is still the latest request
          if (sequenceNumber !== requestSequenceRef.current) {
            return;
          }
          try {
            await updateAbTestConfig({
              abTestConfigId: abTestId,
              body,
            }).unwrap();
          } catch {
            handleUpdateError();
          }
        },
        500,
      ),
    [abTestId, updateAbTestConfig, handleUpdateError],
  );

  useEffect(() => {
    return () => {
      debouncedApiCall.cancel();
    };
  }, [debouncedApiCall]);

  const updateCache = (body: CreateOrUpdateSolveABTestConfig) => {
    dispatch(
      agentAbTestApi.util.updateQueryData(
        'getAbTestConfigById',
        abTestId,
        draft => {
          Object.assign(draft, body);
        },
      ),
    );
  };

  const handleAbTestConfigUpdate = async (
    body: CreateOrUpdateSolveABTestConfig,
    { immediate = false }: { immediate?: boolean } = {},
  ) => {
    // Increment sequence for every update
    requestSequenceRef.current += 1;
    const currentSequence = requestSequenceRef.current;

    updateCache(body);

    if (immediate) {
      debouncedApiCall.cancel();
      try {
        await updateAbTestConfig({ abTestConfigId: abTestId, body }).unwrap();
      } catch {
        handleUpdateError();
      }
    } else {
      debouncedApiCall(body, currentSequence);
    }
  };

  const flushPendingSave = useCallback(() => {
    debouncedApiCall.flush();
  }, [debouncedApiCall]);

  return { flushPendingSave, handleAbTestConfigUpdate, isLoading };
};

export const useGetAgents = (
  product: SolveWidgetProduct = 'workflow_builder',
) => {
  const { data: intentsResponse } = useGetIntentsQuery({
    product,
  });

  const { intents = [] } = intentsResponse || {};
  const enabledIntents =
    intents?.filter(getIsAgentEligibleForAbTestSelection) || [];

  return enabledIntents;
};
